/**
 * Action Executor Service
 * Unified execution engine for all modes (Navigate, Guide, Agent)
 * Handles action execution with safety validation and confirmation
 */

import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom, Subject } from 'rxjs';
import {
  ActionDefinition,
  ClassifiedIntent,
  ActionContext,
  ExecutionResult
} from '../models/app-knowledge.models';
import {
  SafetyGatekeeperService,
  ValidationResult,
  SafetyContext
} from './safety-gatekeeper.service';
import { AppKnowledgeService } from './app-knowledge.service';
import { ElementFinderService } from '../guide/element-finder.service';

export interface ExecutionOptions {
  skipConfirmation?: boolean;
  skipSafetyCheck?: boolean;
  userId?: string;
  userRole?: string;
  mode?: 'navigate' | 'guide' | 'agent';
}

export interface ConfirmationRequest {
  action: ActionDefinition;
  context: ActionContext;
  message: string;
  warnings: string[];
  onConfirm: () => void;
  onCancel: () => void;
}

@Injectable({ providedIn: 'root' })
export class ActionExecutorService {

  // Observables for UI to subscribe to
  public confirmationRequest$ = new Subject<ConfirmationRequest>();
  public executionFeedback$ = new Subject<{
    type: 'info' | 'success' | 'warning' | 'error';
    message: string;
  }>();

  constructor(
    private router: Router,
    private safetyGatekeeper: SafetyGatekeeperService,
    private knowledgeService: AppKnowledgeService,
    private elementFinder: ElementFinderService
  ) {}

  // ============================================================
  // MAIN EXECUTION METHODS
  // ============================================================

  /**
   * Execute an action by ID with context
   */
  async executeAction(
    actionId: string,
    context: ActionContext,
    options: ExecutionOptions = {}
  ): Promise<ExecutionResult> {
    const action = this.knowledgeService.getAction(actionId);
    if (!action) {
      return {
        success: false,
        error: `Action not found: ${actionId}`
      };
    }

    return this.executeActionDefinition(action, context, options);
  }

  /**
   * Execute an action definition with full safety checks
   */
  async executeActionDefinition(
    action: ActionDefinition,
    context: ActionContext,
    options: ExecutionOptions = {}
  ): Promise<ExecutionResult> {
    try {
      // 1. Build safety context
      const safetyContext: SafetyContext = {
        isRecordingMode: options.mode === 'guide',
        isAgentMode: options.mode === 'agent',
        userId: options.userId || 'unknown',
        userRole: options.userRole || 'user'
      };

      // 2. Validate action with safety gatekeeper
      if (!options.skipSafetyCheck) {
        const validation = this.safetyGatekeeper.validateActionDefinition(
          action,
          safetyContext
        );

        if (!validation.safe) {
          return {
            success: false,
            error: `Action blocked: ${validation.reason}`
          };
        }

        // 3. Request confirmation if needed
        if (validation.requiresConfirmation && !options.skipConfirmation) {
          const confirmed = await this.requestConfirmation(
            action,
            context,
            validation
          );

          if (!confirmed) {
            return {
              success: false,
              cancelled: true,
              error: 'User cancelled action'
            };
          }
        }

        // Show warnings if any
        if (validation.warnings && validation.warnings.length > 0) {
          for (const warning of validation.warnings) {
            this.showFeedback('warning', warning);
          }
        }
      }

      // 4. Execute based on action type
      const result = await this.performAction(action, context);

      // 5. Log successful execution
      this.safetyGatekeeper.logAction(
        action.id,
        action.selector,
        { safe: true },
        safetyContext
      );

      return result;

    } catch (error: any) {
      console.error('Action execution failed:', error);
      return {
        success: false,
        error: error.message || 'Unknown error'
      };
    }
  }

  /**
   * Execute based on classified intent
   */
  async executeIntent(
    intent: ClassifiedIntent,
    query: string,
    options: ExecutionOptions = {}
  ): Promise<ExecutionResult> {
    try {
      switch (intent.mode) {
        case 'navigate':
          return this.executeNavigation(intent, options);

        case 'guide':
          return this.executeGuide(intent, query, options);

        case 'agent':
          return this.executeAgent(intent, query, options);

        default:
          return {
            success: false,
            error: `Unknown mode: ${intent.mode}`
          };
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Execution failed'
      };
    }
  }

  // ============================================================
  // MODE-SPECIFIC EXECUTION
  // ============================================================

  /**
   * Execute navigation (Navigate Mode)
   */
  private async executeNavigation(
    intent: ClassifiedIntent,
    options: ExecutionOptions
  ): Promise<ExecutionResult> {
    if (!intent.targetRoute) {
      return {
        success: false,
        error: 'No target route specified'
      };
    }

    try {
      await this.router.navigateByUrl(intent.targetRoute);
      this.showFeedback('success', `Navigated to ${intent.targetRoute}`);

      return {
        success: true,
        message: `Navigated to ${intent.targetRoute}`
      };
    } catch (error: any) {
      return {
        success: false,
        error: `Navigation failed: ${error.message}`
      };
    }
  }

  /**
   * Execute guide workflow (Guide Mode)
   */
  private async executeGuide(
    intent: ClassifiedIntent,
    query: string,
    options: ExecutionOptions
  ): Promise<ExecutionResult> {
    // Guide mode execution is handled by WorkflowPlayerService
    // This just returns metadata to trigger workflow player
    return {
      success: true,
      message: 'Guide workflow ready',
      metadata: {
        mode: 'guide',
        workflowId: intent.workflowId,
        query
      }
    };
  }

  /**
   * Execute agent task (Agent Mode - Foundation Only)
   */
  private async executeAgent(
    intent: ClassifiedIntent,
    query: string,
    options: ExecutionOptions
  ): Promise<ExecutionResult> {
    // Agent mode is not fully implemented yet - return foundation response
    return {
      success: false,
      error: 'Agent mode is not yet implemented. This feature is coming soon.',
      metadata: {
        mode: 'agent',
        actionId: intent.actionId,
        query
      }
    };
  }

  // ============================================================
  // ACTION PERFORMANCE
  // ============================================================

  /**
   * Perform the actual action based on its definition
   */
  private async performAction(
    action: ActionDefinition,
    context: ActionContext
  ): Promise<ExecutionResult> {
    try {
      switch (action.category) {
        case 'navigation':
          return this.performNavigationAction(action, context);

        case 'data':
          return this.performDataAction(action, context);

        case 'messaging':
          return this.performMessagingAction(action, context);

        case 'export':
        case 'import':
          return this.performFileAction(action, context);

        case 'settings':
          return this.performSettingsAction(action, context);

        default:
          return {
            success: false,
            error: `Unknown action category: ${action.category}`
          };
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Action failed'
      };
    }
  }

  /**
   * Navigate to a route
   */
  private async performNavigationAction(
    action: ActionDefinition,
    context: ActionContext
  ): Promise<ExecutionResult> {
    if (!action.route) {
      return { success: false, error: 'No route specified' };
    }

    // Replace route parameters with context values
    let route = action.route;
    if (context.inputs) {
      for (const [key, value] of Object.entries(context.inputs)) {
        route = route.replace(`:${key}`, String(value));
      }
    }

    await this.router.navigateByUrl(route);
    return {
      success: true,
      message: `Navigated to ${route}`
    };
  }

  /**
   * Click an element or perform data action
   */
  private async performDataAction(
    action: ActionDefinition,
    context: ActionContext
  ): Promise<ExecutionResult> {
    if (!action.selector) {
      return { success: false, error: 'No selector specified' };
    }

    // Find the element
    const element = await this.findElement(action.selector);
    if (!element) {
      return {
        success: false,
        error: `Element not found: ${action.selector}`
      };
    }

    // Validate element before interaction
    const validation = this.safetyGatekeeper.validateElementBeforeAction(element);
    if (!validation.safe) {
      return {
        success: false,
        error: `Element interaction blocked: ${validation.reason}`
      };
    }

    // Perform click
    element.click();

    return {
      success: true,
      message: `Clicked ${action.displayName}`
    };
  }

  /**
   * Send a message or communication
   */
  private async performMessagingAction(
    action: ActionDefinition,
    context: ActionContext
  ): Promise<ExecutionResult> {
    // Messaging actions require API calls - foundation only
    return {
      success: false,
      error: 'Messaging actions not yet implemented'
    };
  }

  /**
   * Trigger file export/import
   */
  private async performFileAction(
    action: ActionDefinition,
    context: ActionContext
  ): Promise<ExecutionResult> {
    if (!action.selector) {
      return { success: false, error: 'No selector for file action' };
    }

    const element = await this.findElement(action.selector);
    if (!element) {
      return {
        success: false,
        error: `Export/Import button not found: ${action.selector}`
      };
    }

    // Validate before clicking
    const validation = this.safetyGatekeeper.validateElementBeforeAction(element);
    if (!validation.safe) {
      return {
        success: false,
        error: `File action blocked: ${validation.reason}`
      };
    }

    element.click();

    return {
      success: true,
      message: `Triggered ${action.displayName}`
    };
  }

  /**
   * Change a setting
   */
  private async performSettingsAction(
    action: ActionDefinition,
    context: ActionContext
  ): Promise<ExecutionResult> {
    // Settings changes require confirmation and API calls
    return {
      success: false,
      error: 'Settings actions require user confirmation'
    };
  }

  // ============================================================
  // HELPER METHODS
  // ============================================================

  /**
   * Find element with timeout and fallbacks
   */
  private async findElement(selector: string, timeout = 5000): Promise<HTMLElement | null> {
    try {
      return await this.elementFinder.findBySelector(selector, timeout);
    } catch (error) {
      console.error('Element not found:', selector, error);
      return null;
    }
  }

  /**
   * Request user confirmation for dangerous actions
   */
  private requestConfirmation(
    action: ActionDefinition,
    context: ActionContext,
    validation: ValidationResult
  ): Promise<boolean> {
    return new Promise((resolve) => {
      const request: ConfirmationRequest = {
        action,
        context,
        message: `Are you sure you want to ${action.displayName.toLowerCase()}?`,
        warnings: validation.warnings || [],
        onConfirm: () => resolve(true),
        onCancel: () => resolve(false)
      };

      this.confirmationRequest$.next(request);

      // Auto-cancel after 30 seconds
      setTimeout(() => resolve(false), 30000);
    });
  }

  /**
   * Show feedback to user
   */
  private showFeedback(
    type: 'info' | 'success' | 'warning' | 'error',
    message: string
  ): void {
    this.executionFeedback$.next({ type, message });
  }

  // ============================================================
  // PUBLIC UTILITY METHODS
  // ============================================================

  /**
   * Check if an action can be executed (dry run)
   */
  async canExecuteAction(
    actionId: string,
    options: ExecutionOptions = {}
  ): Promise<{ canExecute: boolean; reason?: string }> {
    const action = this.knowledgeService.getAction(actionId);
    if (!action) {
      return {
        canExecute: false,
        reason: `Action not found: ${actionId}`
      };
    }

    const safetyContext: SafetyContext = {
      isRecordingMode: options.mode === 'guide',
      isAgentMode: options.mode === 'agent',
      userId: options.userId || 'unknown',
      userRole: options.userRole || 'user'
    };

    const validation = this.safetyGatekeeper.validateActionDefinition(
      action,
      safetyContext
    );

    return {
      canExecute: validation.safe,
      reason: validation.reason
    };
  }

  /**
   * Get all executable actions for current context
   */
  getAvailableActions(category?: string): ActionDefinition[] {
    const knowledge = this.knowledgeService.getKnowledgeValue();
    if (!knowledge) return [];

    let actions = Object.values(knowledge.actions) as ActionDefinition[];

    if (category) {
      actions = actions.filter((a: ActionDefinition) => a.category === category);
    }

    // Filter to only safe actions
    return actions.filter((action: ActionDefinition) => {
      const validation = this.safetyGatekeeper.validateActionDefinition(
        action,
        {
          isRecordingMode: false,
          isAgentMode: false,
          userId: 'unknown',
          userRole: 'user'
        }
      );
      return validation.safe;
    });
  }

  /**
   * Cancel any pending confirmations
   */
  cancelPendingConfirmations(): void {
    // Emit an empty confirmation to clear any pending UI
    this.confirmationRequest$.next({
      action: {} as ActionDefinition,
      context: {},
      message: '',
      warnings: [],
      onConfirm: () => {},
      onCancel: () => {}
    });
  }
}
